import { get } from 'lodash';
import { type IFramework } from '@/framework';
import { TextMeasurer } from '@/utils/text-kit';
import {
    LayerType,
    TextWritingType,
    type ILayerProps,
    type IVectorLayerProps,
    type TextContentPadding,
    type VectorTextInfo,
} from '@/version-types';
import { pushHistory, updateLayer } from '../utils';

export interface ITextPadding {
    /**
     * 设置文本颜色
     *
     * @param layerID - 图层 ID
     * @param padding - 预留 padding
     * @param options - 可选；配置选项；`options.writeHistory` 是否写入历史记录
     */
    setTextPadding(
        layerID: string,
        padding: TextContentPadding,
        options?: { writeHistory: boolean },
    ): ILayerProps | void;
}

export class TextPadding implements ITextPadding {
    private prevLayer: ILayerProps | null = null;

    constructor(private framework: IFramework) {}

    private calcLayout(layer: ILayerProps) {
        const { width: layerWidth, height: layerHeight } = layer;
        const textInfo = get(layer, 'data.textInfo') as VectorTextInfo;

        const { writingMode, padding } = textInfo;
        const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

        const textSize = TextMeasurer.measure(layer, {
            keepConstantWidth: isHorizontal,
            keepConstantHeight: !isHorizontal,
            preservePadding: padding!,
        });

        const proactiveWidth = layer.__proactiveWidth || layerWidth;
        const proactiveHeight = layer.__proactiveHeight || layerHeight;

        const nextLayerWidth = textSize.width > proactiveWidth ? textSize.width : proactiveWidth;
        const nextLayerHeight = textSize.height > proactiveHeight ? textSize.height : proactiveHeight;

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
        };
    }

    setTextPadding(layerID: string, padding: TextContentPadding, options = { writeHistory: false }) {
        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        let prevPadding = textInfo.padding;
        if (!prevPadding) {
            prevPadding = { top: 0, right: 0, bottom: 0, left: 0 };
        }

        const nextPadding: TextContentPadding = { ...prevPadding, ...padding };

        const nextTextInfo: VectorTextInfo = { ...textInfo, padding: nextPadding };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { width, height } = this.calcLayout(nextLayer);
        nextLayer.width = width;
        nextLayer.height = height;

        updateLayer(this.framework, nextLayer);

        const { writeHistory } = options;
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer);
            this.prevLayer = null;
        }

        return nextLayer;
    }
}
